import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, FormHelperText, DialogActions, Button, TextField, IconButton, Grid, Typography, Box, MenuItem, Select, FormControl, FormControlLabel, Checkbox } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';

// Helper arrays for months and years
const months = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const years = Array.from({ length: 21 }, (_, i) => (new Date().getFullYear() - 20 + i).toString());

const EditExperienceModal = ({ open, onClose, onSave, experience }) => {


  const [formData, setFormData] = useState({
  title: '',
  organization: '',
  startMonth: '', // Ensure this is an empty string
  startYear: '',  // Ensure this is an empty string
  endMonth: '',   // Ensure this is an empty string
  endYear: '',    // Ensure this is an empty string
  current: false,
});

  // Convert incoming data to set initial values for the modal
  useEffect(() => {
    if (experience) {
      const startDate = new Date(experience.starting);
      const endDate = experience.end ? new Date(experience.end) : null;

      setFormData({
        title: experience.job_title,
        organization: experience.organization,
        startMonth: months[startDate.getMonth()],  // Get month name
        startYear: startDate.getFullYear().toString(),  // Get year
        endMonth: endDate ? months[endDate.getMonth()] : 'January',  // Get month name for end date
        endYear: endDate ? endDate.getFullYear().toString() : new Date().getFullYear().toString(),  // Get year for end date
        current: !experience.end,  // If there's no end date, consider it as current
      });
    }else{
      setFormData(null)
    }
  }, [experience]);

  // const handleChange = (e) => {
  //   const { name, value } = e.target;
  //   setFormData((prev) => ({
  //     ...prev,
  //     [name]: value,
  //   }));
  // };

  const handleChange = (e) => {
  const { name, value } = e.target;
  let error = "";

  switch (name) {
    case "title":
      error = value.trim() === "" ? "Title is required." : "";
      break;
    case "organization":
      error = value.trim() === "" ? "Organization is required." : "";
      break;
    case "startYear":
    case "endYear":
      if (!/^(19|20)\d{2}$/.test(value)) {
        error = "Invalid year format.";
      } else if (name === "endYear" && parseInt(value) < parseInt(formData.startYear)) {
        error = "End year cannot be before start year.";
      }
      break;
    case "startMonth":
    case "endMonth":
      if (!months.includes(value)) {
        error = "Invalid month selection.";
      }
      break;
    default:
      break;
  }

  setFormData((prev) => {
    const updatedData = {
      ...prev,
      [name]: value,
      [`${name}Error`]: error,
    };

    // Add validation for end date after start date
    if (updatedData.endYear === updatedData.startYear) {
      const startMonthIndex = months.indexOf(updatedData.startMonth);
      const endMonthIndex = months.indexOf(updatedData.endMonth);

      if (endMonthIndex < startMonthIndex) {
        updatedData.endMonthError = "End month must be after start month in the same year.";
      } else {
        updatedData.endMonthError = "";
      }
    } else if (parseInt(updatedData.endYear) < parseInt(updatedData.startYear)) {
      updatedData.endYearError = "End year cannot be before start year.";
    } else {
      updatedData.endYearError = "";
    }

    return updatedData;
  });
};



  const handleCheckboxChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      current: e.target.checked,
    }));
  };

  const handleSubmit = () => {
    // Format the start date
    const startDate = `${formData.startYear}-${String(months.indexOf(formData.startMonth) + 1).padStart(2, '0')}-01T00:00:00.000Z`;

    // If current, leave the end date as null
    const endDate = formData.current
      ? null
      : `${formData.endYear}-${String(months.indexOf(formData.endMonth) + 1).padStart(2, '0')}-01T00:00:00.000Z`;

    const formattedExperience = {
      job_title: formData.title,
      organization: formData.organization,
      starting: startDate,
      end: endDate,  // Leave as null if current job
      _id: experience ? experience._id : undefined, // Preserve the existing ID for editing
    };

    onSave(formattedExperience);
    setFormData({
      title: '',
      organization: '',
      startMonth: 'January',
      startYear: new Date().getFullYear().toString(),
      endMonth: 'January',
      endYear: new Date().getFullYear().toString(),
      current: false,
    });
  };

  const isFormValid = () => {
  // Check that the start date is valid (start month and start year are both selected)
  const isStartDateValid = formData?.startMonth && formData?.startYear;
  
  // If not currently working, check that the end date (end month and end year) is valid
  const isEndDateValid = formData?.current || (formData?.endMonth && formData?.endYear);

  return (
    isStartDateValid &&  // Start date must be valid
    !formData?.titleError &&
    !formData?.organizationError &&
    !formData?.startMonthError &&
    !formData?.startYearError &&
    (!formData?.current ? (!formData?.endMonthError && !formData?.endYearError) : true) &&
    formData?.title?.trim() !== "" && formData?.organization?.trim() !== "" &&
    isEndDateValid  // End date is also validated if not current
  );
};


  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper" sx={{ borderRadius: '16px' }} >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 3 }}>
        <Typography variant="">{experience ? 'Edit Experience' : 'Add Experience'}</Typography>
        <IconButton edge="end" color="inherit" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>

          <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom>Title<Box component="span" sx={{ color: 'error.main' }}>*</Box></Typography>
            <TextField
              fullWidth
              name="title"
              value={formData?.title}
              onChange={handleChange}
              placeholder="Mortgage Broker"
              variant="outlined"
              required
              sx={{ mb: 2 }}
              error={!!formData?.titleError}
              helperText={formData?.titleError}
            />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom>Company/Organization<Box component="span" sx={{ color: 'error.main' }}>*</Box></Typography>
            <TextField
              fullWidth
              name="organization"
              value={formData?.organization}
              onChange={handleChange}
              placeholder="Doe Mortgage Solutions"
              variant="outlined"
              required
              sx={{ mb: 2 }}
              error={!!formData?.organizationError}
              helperText={formData?.organizationError}
            />
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={<Checkbox checked={formData?.current} onChange={handleCheckboxChange} color="primary" sx={{
                '&.Mui-checked': { color: 'green', },
              }} />}
              label="I am currently working in this role."
            />
          </Grid>


          {/* <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom>Start Date<Box component="span" sx={{ color: 'error.main' }}>*</Box>
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>

                <FormControl fullWidth variant="outlined" sx={{ mb: 2 }} error={!!formData.startMonthError}>
                  <Select
                    name="startMonth"
                    value={formData?.startMonth}
                    onChange={handleChange}
                    // displayEmpty
                    sx={{ borderRadius: 1 }}
                    placeholder
                  >
                    {months.map((month) => (
                      <MenuItem key={`start-${month}`} value={month}>{month}</MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>{formData.startMonthError}</FormHelperText>
                </FormControl>

              </Grid>
              <Grid item xs={6}>

                <FormControl fullWidth variant="outlined" sx={{ mb: 2 }} error={!!formData.startYearError}>
                  <Select
                    name="startYear"
                    value={formData.startYear}
                    onChange={handleChange}
                    // displayEmpty
                    sx={{ borderRadius: 1 }}
                  >
                    {years.map((year) => (
                      <MenuItem key={`start-${year}`} value={year}>{year}</MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>{formData.startYearError}</FormHelperText>
                </FormControl>

              </Grid>
            </Grid>
          </Grid>

          {!formData.current && (
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                End Date
                <Box component="span" sx={{ color: 'error.main' }}>*</Box>
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>

                  <FormControl fullWidth variant="outlined" sx={{ mb: 2 }} error={!!formData.endMonthError}>
                    <Select
                      name="endMonth"
                      value={formData.endMonth}
                      onChange={handleChange}
                      // displayEmpty
                      sx={{ borderRadius: 1 }}
                      placeholder="gggggggggg"
                    >
                      {months.map((month) => (
                        <MenuItem key={`end-${month}`} value={month}>{month}</MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>{formData.endMonthError}</FormHelperText>
                  </FormControl>


                </Grid>
                <Grid item xs={6}>

                  <FormControl fullWidth variant="outlined" sx={{ mb: 2 }} error={!!formData.endYearError}>
                    <Select
                      name="endYear"
                      value={formData.endYear}
                      onChange={handleChange}
                      // displayEmpty
                      sx={{ borderRadius: 1 }}
                    >
                      {years.map((year) => (
                        <MenuItem key={`end-${year}`} value={year}>{year}</MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>{formData.endYearError}</FormHelperText>
                  </FormControl>

                </Grid>
              </Grid>
            </Grid>
          )} */}


          <Grid item xs={12}>
  <Typography variant="subtitle2" gutterBottom>
    Start Date
    <Box component="span" sx={{ color: 'error.main' }}>*</Box>
  </Typography>
  <Grid container spacing={2}>
    <Grid item xs={6}>
      <FormControl fullWidth variant="outlined" sx={{ mb: 2 }} error={!!formData?.startMonthError}>
        <Select
          name="startMonth"
          value={formData?.startMonth || ''}  // Set initial empty string value for placeholder
          onChange={handleChange}
          displayEmpty
          sx={{ borderRadius: 1 }}
        >
          <MenuItem value="" >Start Month</MenuItem>
          {months.map((month) => (
            <MenuItem key={`start-${month}`} value={month}>{month}</MenuItem>
          ))}
        </Select>
        <FormHelperText>{formData?.startMonthError}</FormHelperText>
      </FormControl>
    </Grid>
    <Grid item xs={6}>
      <FormControl fullWidth variant="outlined" sx={{ mb: 2 }} error={!!formData?.startYearError}>
        <Select
          name="startYear"
          value={formData?.startYear || ''}  // Set initial empty string value for placeholder
          onChange={handleChange}
          displayEmpty
          sx={{ borderRadius: 1 }}
        >
          <MenuItem value="">Start Year</MenuItem>
          {years.map((year) => (
            <MenuItem key={`start-${year}`} value={year}>{year}</MenuItem>
          ))}
        </Select>
        <FormHelperText>{formData?.startYearError}</FormHelperText>
      </FormControl>
    </Grid>
  </Grid>
</Grid>

{!formData?.current && (
  <Grid item xs={12}>
    <Typography variant="subtitle2" gutterBottom>
      End Date
      <Box component="span" sx={{ color: 'error.main' }}>*</Box>
    </Typography>
    <Grid container spacing={2}>
      <Grid item xs={6}>
        <FormControl fullWidth variant="outlined" sx={{ mb: 2 }} error={!!formData?.endMonthError}>
          <Select
            name="endMonth"
            value={formData?.endMonth || ''}  // Set initial empty string value for placeholder
            onChange={handleChange}
            displayEmpty
            sx={{ borderRadius: 1 }}
          >
            <MenuItem value="">End Month</MenuItem>
            {months.map((month) => (
              <MenuItem key={`end-${month}`} value={month}>{month}</MenuItem>
            ))}
          </Select>
          <FormHelperText>{formData?.endMonthError}</FormHelperText>
        </FormControl>
      </Grid>
      <Grid item xs={6}>
        <FormControl fullWidth variant="outlined" sx={{ mb: 2 }} error={!!formData?.endYearError}>
          <Select
            name="endYear"
            value={formData?.endYear || ''}  // Set initial empty string value for placeholder
            onChange={handleChange}
            displayEmpty
            sx={{ borderRadius: 1 }}
          >
            <MenuItem value="">End Year</MenuItem>
            {years.map((year) => (
              <MenuItem key={`end-${year}`} value={year}>{year}</MenuItem>
            ))}
          </Select>
          <FormHelperText>{formData?.endYearError}</FormHelperText>
        </FormControl>
      </Grid>
    </Grid>
  </Grid>
)}



        </Grid>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!isFormValid()}
          sx={{
            borderRadius: 8,
            px: 4,
            backgroundColor: '#f59e0b',
            '&:hover': { backgroundColor: '#d97706' },
          }}
        >SAVE
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditExperienceModal;
